import { useEffect, useState } from 'react';
import type { ReactNode, Ref, ReactElement } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';

import { styled } from '../styles';

import Box from '../Box';
import Typography from '../Typography';

const Transition = (
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>
) => <Slide direction="left" ref={ref} {...props} />;

const RootDialog = styled(Dialog, { shouldForwardProp: (prop) => prop !== 'isSide' })<{
  isSide?: boolean;
}>(({ theme: { breakpoints }, isSide }) => ({
  [breakpoints.up('md')]: /* istanbul ignore next */ isSide && {
    width: 640,
    left: 'auto',
  },
}));

const Header = styled(DialogTitle)(({ theme: { spacing, palette, breakpoints } }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing(1),
  minHeight: 56,
  alignItems: 'center',
  padding: spacing(0, 2),
  borderBottom: `1px solid ${palette.layout.line.divider}`,
  [breakpoints.up('sm')]: {
    minHeight: 64,
    padding: spacing(0, 4),
  },
}));

const ActionContainer = styled('div')({
  flexShrink: 0,
});

type Variant = 'default' | 'noPadding';
type AlignContent = 'center' | 'left' | 'fullWidth';
type Background = 'normal' | 'alternative';

const ScrollContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !['variant', 'backgroundVariant'].includes(prop),
})<{ variant?: Variant; backgroundVariant?: Background }>(
  ({ theme: { spacing, breakpoints, palette }, variant, backgroundVariant = 'normal' }) => ({
    padding: variant !== 'noPadding' ? spacing(4, 2) : 0,
    height: 'calc(100% - 64px)',
    overflowY: 'auto',
    backgroundColor: {
      normal: palette.layout.background.navigation,
      alternative: palette.layout.background.navigationAlternative,
    }[backgroundVariant],
    [breakpoints.up('sm')]: {
      padding: variant !== 'noPadding' ? spacing(4, 4) : 0,
    },
  })
);

const Container = styled('div', {
  shouldForwardProp: (prop: string) => !['variant', 'alignContent'].includes(prop),
})<{ alignContent: AlignContent }>(({ alignContent, theme: { spacing } }) => ({
  maxWidth: 768,
  ...{
    center: {
      margin: '0 auto',
      width: '100%',
    },
    left: {
      marginLeft: spacing(2),
    },
    fullWidth: {
      maxWidth: '100%',
    },
  }[alignContent],
}));

export interface Props {
  children?: ReactNode;
  title?: string;
  onClose?: () => void;
  onEndClose?: () => void;
  isSide?: boolean;
  isOpen?: boolean;
  variant?: Variant;
  alignContent?: AlignContent;
  background?: Background;
  isBackdropHidden?: boolean;
  translations: {
    closeButtonLabel: string;
  };
  isKeepMounted?: boolean;
  actionElement?: ReactNode;
}

/* istanbul ignore next */
const noop = () => {};

const ContentDialog = ({
  children,
  title,
  translations,
  variant,
  alignContent = 'center',
  background = 'normal',
  actionElement,
  isOpen,
  isSide,
  isBackdropHidden,
  isKeepMounted,
  onClose = noop,
  onEndClose = noop,
}: Props) => {
  const [recordedScrollHeight, setRecordedScrollHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!recordedScrollHeight && isOpen === true) {
      setRecordedScrollHeight(window.scrollY);
    }
    /* istanbul ignore next */
    if (recordedScrollHeight && isOpen === false) {
      window.scrollTo(0, recordedScrollHeight);
    }
  }, [recordedScrollHeight, isOpen]);

  return (
    <RootDialog
      isSide={isSide}
      open={isOpen}
      fullScreen
      onClose={onClose}
      TransitionComponent={Transition}
      TransitionProps={{
        onExited() {
          onEndClose?.();
        },
      }}
      hideBackdrop={isBackdropHidden}
      keepMounted={isKeepMounted}
    >
      <Header component="div">
        <div>
          <IconButton
            aria-label={translations.closeButtonLabel}
            size="large"
            color="inherit"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
        {title && (
          <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }} component="h1">
            {title}
          </Typography>
        )}
        {actionElement && <ActionContainer>{actionElement}</ActionContainer>}
      </Header>
      <ScrollContainer variant={variant} backgroundVariant={background} data-scrollable-element>
        <Container alignContent={alignContent}>{children}</Container>
      </ScrollContainer>
    </RootDialog>
  );
};
export default ContentDialog;
